import { useEffect, useState } from 'react';
import { Timer, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SessionTimerProps {
  startTime: string;
  expiresAt: string;
}

export function SessionTimer({ startTime, expiresAt }: SessionTimerProps) {
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [progress, setProgress] = useState(100);
  const [showTooltip, setShowTooltip] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const start = new Date(startTime).getTime();
      const end = new Date(expiresAt).getTime();
      const total = end - start;
      const elapsed = now - start;
      const remaining = end - now;

      if (remaining <= 0) {
        setTimeLeft(t('session.timer.expired'));
        setProgress(0);
        return;
      }

      // Calculate progress
      const currentProgress = Math.max(0, Math.min(100, ((total - elapsed) / total) * 100));
      setProgress(currentProgress);

      // Format time left in a user-friendly way
      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeLeft(t('session.timer.days', { count: days }));
      } else if (hours > 0) {
        setTimeLeft(t('session.timer.hours', { count: hours }));
      } else {
        setTimeLeft(t('session.timer.minutes', { count: minutes }));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [startTime, expiresAt, t]);

  return (
    <div className="rounded-lg bg-white/80 p-4 shadow-sm backdrop-blur-sm">
      <div className="flex items-center gap-2">
        <Timer className="h-5 w-5 text-gray-400" />
        <span className="font-medium text-gray-600">{timeLeft}</span>
        <button
          className="rounded-full p-1 hover:bg-gray-100 transition-colors"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          aria-label={t('session.timer.info.aria')}
        >
          <Info className="h-4 w-4 text-gray-400" />
        </button>
        {showTooltip && (
          <div className="absolute mt-2 transform translate-y-8 bg-gray-900 text-white text-sm rounded-lg py-2 px-3 shadow-lg z-10">
            {t('session.timer.info.tooltip')}
          </div>
        )}
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full transition-all duration-1000 bg-gray-300`}
          style={{ width: `${progress}%`, opacity: 0.5 }}
        />
      </div>
    </div>
  );
}